import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { EnvelopeIcon, PhoneIcon, MapPinIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Instagram, Facebook, Linkedin } from 'lucide-react';

import useDocumentMetadata from '../hooks/useDocumentMetadata';

const Contact = () => {
  useDocumentMetadata(
    'Contact Us - Dakhla',
    'Get in touch with the Dakhla team. Send us your queries, feedback, or support requests regarding university admissions in Pakistan.'
  );

  const navigate = useNavigate();
  const location = useLocation();

  const [user] = useState(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  });

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Handle URL query parameters to auto-select subject dropdown
  // Handle URL query parameters & auto-scroll to contact form below hero
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const type = searchParams.get('type');
    
    let mappedSubject = '';
    if (type === 'partner') {
      mappedSubject = 'university-partnership';
    } else if (type === 'feedback') {
      mappedSubject = 'feedback';
    } else if (type === 'bug') {
      mappedSubject = 'bug-report';
    }
    
    setFormData(prev => ({
      ...prev,
      subject: mappedSubject
    }));

    if (type || location.hash === '#contact-form') {
      setTimeout(() => {
        const el = document.getElementById('contact-form');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location.search, location.hash]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          userId: user?.id || null,
          userType: user?.role || 'guest'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for reaching out! We have received your message and will get back to you within 24 hours.'
        });
        // Reset form if user is guest, otherwise keep name/email
        if (!user) {
          setFormData({ name: '', email: '', subject: '', message: '' });
        } else {
          setFormData(prev => ({ ...prev, subject: '', message: '' }));
        }
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.error || 'Failed to send message. Please try again.'
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send message. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };



  // Shared input class — matches Login/Signup design system
  const inputClass = (disabled = false) =>
    `appearance-none block w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-medium transition-premium text-sm${
      disabled ? ' bg-slate-100 cursor-not-allowed text-slate-500' : ''
    }`;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="relative bg-[#1E256F] text-white py-20 overflow-hidden shadow-sm">
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(to right, rgba(255, 255, 255, 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.07) 1px, transparent 1px)",
            backgroundSize: "24px 24px"
          }}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-tight mb-4">
            Contact Us
          </h1>
          <p className="text-slate-200 text-base sm:text-lg max-w-2xl mx-auto font-normal">
            Have questions or feedback? We'd love to hear from you. Send us a message and we'll respond promptly.
          </p>
        </div>
      </div>

      <div id="contact-form" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 scroll-mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
          {/* Contact Form */}
          <div className="card relative overflow-hidden lg:col-span-6">
            <h2 className="text-xl font-bold text-slate-800 mb-5">Send us a Message</h2>

            {/* Status Message */}
            {submitStatus && (
              <div className={`p-3.5 rounded-xl mb-5 text-sm font-semibold flex items-center gap-2 border ${
                submitStatus.type === 'success'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-rose-50 border-rose-200 text-rose-700'
              }`}>
                <span>{submitStatus.type === 'success' ? '✓' : '⚠️'}</span>
                <span>{submitStatus.message}</span>
              </div>
            )}

            {/* User Info Banner for authenticated users */}
            {user && (
              <div className="mb-5 p-3.5 bg-primary-50 border border-primary-100 rounded-xl">
                <h3 className="text-xs font-bold text-primary-700 mb-1.5 uppercase tracking-wide">Sending as</h3>
                <p className="text-sm font-semibold text-slate-800">{user.name}</p>
                <p className="text-xs text-slate-500">{user.email} · {user.role === 'student' ? 'Student' : 'University'}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-slate-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!!user}
                  className={inputClass(!!user)}
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!!user}
                  className={inputClass(!!user)}
                  placeholder="you@example.com"
                />
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-xs font-semibold text-slate-700 mb-1">
                  Subject
                </label>
                <div className="relative">
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className={`${inputClass()} pr-10 appearance-none`}
                  >
                    <option value="">Select a subject</option>
                    <option value="application-help">Application Help</option>
                    <option value="technical-support">Technical Support</option>
                    <option value="university-partnership">University Partnership</option>
                    <option value="exam-related">Exam Related</option>
                    <option value="general-inquiry">General Inquiry</option>
                    <option value="feedback">Feedback</option>
                    <option value="bug-report">Report a Bug</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                    <ChevronDownIcon className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-xs font-semibold text-slate-700 mb-1">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  className={inputClass()}
                  placeholder="Tell us how we can help you..."
                />
              </div>

              {/* Submit */}
              <div className="pt-1">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent rounded-xl text-sm font-semibold text-white shadow-md active:scale-[0.98] transition-premium cursor-pointer ${
                    isSubmitting
                      ? 'bg-slate-400 cursor-not-allowed shadow-none'
                      : 'bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-700 hover:to-indigo-700 shadow-primary-500/20 hover:shadow-lg hover:shadow-primary-500/25 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Sending...</span>
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Contact Information */}
          <div className="flex flex-col gap-4 lg:col-span-4">
            <div className="card relative overflow-hidden">
              <h2 className="text-xl font-bold text-slate-800 mb-5">Contact Information</h2>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-primary-50 border border-primary-100">
                    <EnvelopeIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-800">Email</h3>
                    <a href="mailto:support.dakhla@gmail.com" className="text-sm text-slate-500 hover:text-primary-600 hover:underline">
                      support.dakhla@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-lg bg-primary-50 border border-primary-100">
                    <PhoneIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-800">Phone</h3>
                    <p className="text-sm text-slate-500">+92 322 3437878</p>
                  </div>
                </div>

                {/* Office address hidden per configuration */}

                <div className="border-t border-slate-100 pt-5 mt-5">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Connect With Us</h3>
                  <div className="flex gap-3">
                    <a
                      href="https://wa.me/message/NFSXJKGLHLPGI1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 bg-transparent text-slate-400 hover:text-emerald-600 hover:border-emerald-600 hover:scale-105 transition-all duration-150"
                      title="WhatsApp"
                    >
                      <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                      </svg>
                    </a>
                    <a
                      href="https://www.instagram.com/dakhla.pk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 bg-transparent text-slate-400 hover:text-slate-800 hover:border-slate-800 hover:scale-105 transition-all duration-150"
                      title="Instagram"
                    >
                      <Instagram className="h-5 w-5" />
                    </a>
                    <a
                      href="https://www.facebook.com/dakhla.pakistan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 bg-transparent text-slate-400 hover:text-slate-800 hover:border-slate-800 hover:scale-105 transition-all duration-150"
                      title="Facebook"
                    >
                      <Facebook className="h-5 w-5" />
                    </a>
                    <a
                      href="https://www.linkedin.com/company/dakhla-pk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 bg-transparent text-slate-400 hover:text-slate-800 hover:border-slate-800 hover:scale-105 transition-all duration-150"
                      title="LinkedIn"
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="card bg-primary-50/60 border-primary-100">
              <h3 className="text-sm font-bold text-primary-800 mb-2">Office Hours</h3>
              <div className="space-y-1 text-sm text-primary-700">
                <p>Monday – Friday: 9:00 AM – 6:00 PM</p>
                <p>Saturday & Sunday: Closed</p>
              </div>
            </div>

            <div className="card bg-emerald-50/60 border-emerald-100">
              <h3 className="text-sm font-bold text-emerald-800 mb-1">Quick Response</h3>
              <p className="text-sm text-emerald-700">
                For urgent queries, reach out to us via WhatsApp or email.<br />
                We typically respond within 24 hours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;